import React, { useState, useEffect, useRef } from 'react'
import { IoCheckmark } from 'react-icons/io5'
import { MobileSearchFilters as FiltersType } from './hooks/useMobileSearch'
import useApiService from '../../services/api'
import { MobileColors } from '../../styles/mobileTokens'
import {
  SidePanel,
  PanelOverlay,
  PanelHeader,
  PanelContent,
  PanelFooter,
  Button,
} from './MobileSearch.styles'
import styled from 'styled-components'

interface MobileSearchFiltersProps {
  isOpen: boolean
  filters: FiltersType
  onClose: () => void
  onApply: (filters: FiltersType) => void
}

// Multi-select state
interface MultiSelectFilters {
  categories: string[]
  types: string[]
}

const CollapsibleSection = styled.div`
  margin-bottom: 12px;
`

const SectionHeader = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border: 1px solid
    ${(props) =>
      props.$isOpen ? MobileColors.primary : MobileColors.borderDefault};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(props) =>
      props.$isOpen ? MobileColors.primary : MobileColors.borderMedium};
  }

  &:active {
    background: ${MobileColors.backgroundOverlay};
  }
`

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${MobileColors.textPrimary};
`

const PanelTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SearchResultsLabel = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${MobileColors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SectionValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
`

const SectionContent = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  background: white;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 8px;
  margin-top: 8px;
`

const OptionsList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 16px 12px 16px;
`

const OptionItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  margin-bottom: 4px;
  background: white;
  border: 1px solid
    ${(props) =>
      props.$isSelected
        ? '${MobileColors.accent}'
        : '${MobileColors.borderMedium}'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${MobileColors.textPrimary};
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    background: ${MobileColors.backgroundOverlay};
    border-color: ${MobileColors.textMuted};
  }

  &:active {
    background: ${MobileColors.backgroundLight};
  }
`

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  font-weight: ${(props: { $isSelected?: boolean }) =>
    props.$isSelected ? '500' : '400'};
`

const CheckmarkIcon = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.accent};
  font-size: 18px;
`

export const MobileSearchFilters: React.FC<MobileSearchFiltersProps> = ({
  isOpen,
  filters,
  onClose,
  onApply,
}) => {
  const api = useApiService()
  const { data: categoriesData } = api.useGetActiveCompanyCategoriesAndTypes()
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Convert comma-separated values to arrays for multi-select
  const [multiSelectFilters, setMultiSelectFilters] =
    useState<MultiSelectFilters>({
      categories: filters.category
        ? filters.category.split(',').filter(Boolean)
        : [],
      types: filters.type ? filters.type.split(',').filter(Boolean) : [],
    })

  const [expandedSections, setExpandedSections] = useState<{
    category: boolean
    type: boolean
  }>({
    category: false,
    type: false,
  })

  // Update multi-select filters when prop filters change
  useEffect(() => {
    setMultiSelectFilters({
      categories: filters.category
        ? filters.category.split(',').filter(Boolean)
        : [],
      types: filters.type ? filters.type.split(',').filter(Boolean) : [],
    })
  }, [filters])

  // Lock body scroll and manage focus when panel is open
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      // Lock body scroll
      document.body.style.overflow = 'hidden'

      // Focus panel for screen readers
      panelRef.current?.focus()
    } else {
      // Restore body scroll
      document.body.style.overflow = ''

      // Restore focus to previously focused element
      previousFocusRef.current?.focus()
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const toggleSection = (section: 'category' | 'type') => {
    setExpandedSections((prev) => {
      const isCurrentlyOpen = prev[section]
      // Close the clicked section if it's open, or open it and close the other
      if (isCurrentlyOpen) {
        return {
          ...prev,
          [section]: false,
        }
      } else {
        // Open this section and close the other
        return {
          category: section === 'category',
          type: section === 'type',
        }
      }
    })
  }

  const handleCategoryToggle = (categorySlug: string) => {
    setMultiSelectFilters((prev) => {
      const isSelected = prev.categories.includes(categorySlug)
      const newCategories = isSelected
        ? prev.categories.filter((c) => c !== categorySlug)
        : [...prev.categories, categorySlug]

      return {
        ...prev,
        categories: newCategories,
        // Clear types when categories change - they will need to reselect valid types
        types: [],
      }
    })
    // Keep section open for multi-select
  }

  const handleCategoryAll = () => {
    setMultiSelectFilters((prev) => ({
      ...prev,
      categories: [],
      types: [],
    }))
    // Keep section open for multi-select
  }

  const handleTypeToggle = (typeSlug: string) => {
    setMultiSelectFilters((prev) => {
      const isSelected = prev.types.includes(typeSlug)
      const newTypes = isSelected
        ? prev.types.filter((t) => t !== typeSlug)
        : [...prev.types, typeSlug]

      return {
        ...prev,
        types: newTypes,
      }
    })
    // Keep section open for multi-select
  }

  const handleTypeAll = () => {
    setMultiSelectFilters((prev) => ({
      ...prev,
      types: [],
    }))
    // Keep section open for multi-select
  }

  const handleApply = () => {
    // Convert arrays to comma-separated strings for backend
    const convertedFilters: FiltersType = {
      category:
        multiSelectFilters.categories.length > 0
          ? multiSelectFilters.categories.join(',')
          : undefined,
      type:
        multiSelectFilters.types.length > 0
          ? multiSelectFilters.types.join(',')
          : undefined,
    }
    onApply(convertedFilters)
    onClose()
  }

  const handleCancel = () => {
    setMultiSelectFilters({
      categories: filters.category
        ? filters.category.split(',').filter(Boolean)
        : [],
      types: filters.type ? filters.type.split(',').filter(Boolean) : [],
    })
    onClose()
  }

  // Get available types based on selected categories
  const availableTypes = React.useMemo(() => {
    if (!categoriesData) {
      return []
    }

    // If no categories selected, show all types
    if (multiSelectFilters.categories.length === 0) {
      const allTypes: any[] = []
      categoriesData.forEach((category) => {
        category.sub_categories.forEach((subCategory) => {
          if (!allTypes.find((t) => t.id === subCategory.id)) {
            allTypes.push(subCategory)
          }
        })
      })
      return allTypes
    }

    // Show types for selected categories (deduplicate by ID)
    const typesMap = new Map<number, any>()
    multiSelectFilters.categories.forEach((catSlug) => {
      const category = categoriesData.find((cat) => cat.slug === catSlug)
      category?.sub_categories.forEach((subCat) => {
        typesMap.set(subCat.id, subCat)
      })
    })
    return Array.from(typesMap.values())
  }, [categoriesData, multiSelectFilters.categories])

  // Sort categories alphabetically by name
  const sortedCategories = React.useMemo(() => {
    if (!categoriesData) return []
    return [...categoriesData].sort((a, b) => a.name.localeCompare(b.name))
  }, [categoriesData])

  // Sort types alphabetically by display name (with category prefix for "Other")
  const sortedTypes = React.useMemo(() => {
    return [...availableTypes]
      .map((type) => {
        // Add category prefix for "Other" types to disambiguate
        if (type.name === 'Other') {
          const category = categoriesData?.find((cat) =>
            cat.sub_categories.some((sub) => sub.id === type.id)
          )
          return {
            ...type,
            displayName: category ? `${category.name} - Other` : type.name,
          }
        }
        return {
          ...type,
          displayName: type.name,
        }
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [availableTypes, categoriesData])

  // Get display labels
  const getCategoryLabel = () => {
    if (multiSelectFilters.categories.length === 0) return 'All'
    if (multiSelectFilters.categories.length === 1) {
      const category = categoriesData?.find(
        (cat) => cat.slug === multiSelectFilters.categories[0]
      )
      return category?.name || 'All'
    }
    return `${multiSelectFilters.categories.length} selected`
  }

  const getTypeLabel = () => {
    if (multiSelectFilters.types.length === 0) return 'All'
    if (multiSelectFilters.types.length === 1) {
      const type = sortedTypes.find(
        (t) => t.slug === multiSelectFilters.types[0]
      )
      return type?.displayName || 'All'
    }
    return `${multiSelectFilters.types.length} selected`
  }

  return (
    <>
      <PanelOverlay $isOpen={isOpen} onClick={onClose} />
      <SidePanel
        $isOpen={isOpen}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filter search results"
        tabIndex={-1}
      >
        <PanelHeader>
          <PanelTitleContainer>
            <SearchResultsLabel>SEARCH RESULTS</SearchResultsLabel>
            <h3
              id="filter-panel-title"
              className="m-0 text-lg font-semibold text-fg"
            >
              Filter
            </h3>
          </PanelTitleContainer>
        </PanelHeader>

        <PanelContent>
          {/* Category Filter */}
          <CollapsibleSection>
            <SectionHeader
              type="button"
              $isOpen={expandedSections.category}
              onClick={() => toggleSection('category')}
            >
              <SectionLabel>
                <SectionTitle>Opp Category:</SectionTitle>
                <SectionValue>{getCategoryLabel()}</SectionValue>
              </SectionLabel>
            </SectionHeader>
            <SectionContent $isOpen={expandedSections.category}>
              <OptionsList>
                {/* All option - clears all selections */}
                <OptionItem
                  $isSelected={multiSelectFilters.categories.length === 0}
                  onClick={handleCategoryAll}
                >
                  <OptionLabel
                    $isSelected={multiSelectFilters.categories.length === 0}
                  >
                    All
                  </OptionLabel>
                  <CheckmarkIcon
                    $visible={multiSelectFilters.categories.length === 0}
                  >
                    <IoCheckmark />
                  </CheckmarkIcon>
                </OptionItem>

                {sortedCategories?.map((category) => (
                  <OptionItem
                    key={category.id}
                    $isSelected={multiSelectFilters.categories.includes(
                      category.slug
                    )}
                    onClick={() => handleCategoryToggle(category.slug)}
                  >
                    <OptionLabel
                      $isSelected={multiSelectFilters.categories.includes(
                        category.slug
                      )}
                    >
                      {category.name}
                    </OptionLabel>
                    <CheckmarkIcon
                      $visible={multiSelectFilters.categories.includes(
                        category.slug
                      )}
                    >
                      <IoCheckmark />
                    </CheckmarkIcon>
                  </OptionItem>
                ))}
              </OptionsList>
            </SectionContent>
          </CollapsibleSection>

          {/* Type Filter */}
          <CollapsibleSection>
            <SectionHeader
              type="button"
              $isOpen={expandedSections.type}
              onClick={() => toggleSection('type')}
            >
              <SectionLabel>
                <SectionTitle>Type:</SectionTitle>
                <SectionValue>{getTypeLabel()}</SectionValue>
              </SectionLabel>
            </SectionHeader>
            <SectionContent $isOpen={expandedSections.type}>
              <OptionsList>
                {/* All option - clears type selections */}
                <OptionItem
                  $isSelected={multiSelectFilters.types.length === 0}
                  onClick={handleTypeAll}
                >
                  <OptionLabel
                    $isSelected={multiSelectFilters.types.length === 0}
                  >
                    All
                  </OptionLabel>
                  <CheckmarkIcon
                    $visible={multiSelectFilters.types.length === 0}
                  >
                    <IoCheckmark />
                  </CheckmarkIcon>
                </OptionItem>

                {sortedTypes.map((type) => (
                  <OptionItem
                    key={type.id}
                    $isSelected={multiSelectFilters.types.includes(type.slug)}
                    onClick={() => handleTypeToggle(type.slug)}
                  >
                    <OptionLabel
                      $isSelected={multiSelectFilters.types.includes(type.slug)}
                    >
                      {type.displayName}
                    </OptionLabel>
                    <CheckmarkIcon
                      $visible={multiSelectFilters.types.includes(type.slug)}
                    >
                      <IoCheckmark />
                    </CheckmarkIcon>
                  </OptionItem>
                ))}
              </OptionsList>
            </SectionContent>
          </CollapsibleSection>
        </PanelContent>

        <PanelFooter>
          <Button type="button" $variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" $variant="primary" onClick={handleApply}>
            Apply
          </Button>
        </PanelFooter>
      </SidePanel>
    </>
  )
}
